/**
 * 초대 수락 (지시54) — **로그인 밖이다**(통합 브리프 ④).
 *
 * 아직 계정이 없는 사람이 쓰는 화면이라 `POST /v1/invites/accept` 를 인증 없이 부른다.
 * 그래서 `App` 의 인증 관문 **앞에서** 그려진다 — 관문 뒤에 두면 로그인하라고 튕기는데,
 * 로그인할 계정이 없어서 여기 온 사람이다.
 *
 * ## 실패 사유를 갈라 추측하지 않는다
 *
 * "없는 토큰" 과 "만료된 토큰" 이 **같은 문구로 온다.** 일부러 그렇게 한 것이므로
 * (있는 토큰인지 캐볼 수 없게) 화면도 갈라 말하지 않는다. 다른 화면에서는 원인을
 * 구분해 말하는 것이 규칙이지만, 여기서는 **구분하지 않는 것이 규칙**이다.
 *
 * 429 는 다르다 — 속도 제한이고 `retry-after` 가 실려 온다. 그건 기다리면 되는
 * 일이라 얼마나 기다릴지 말해 준다.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '../../lib/useApiClient.tsx';
import { ApiError } from '../../lib/apiClient.tsx';
import { strings } from '../../lib/strings.tsx';
import './access.css';

const t = strings.access;

/** 주소에서 토큰을 꺼낸다. 없으면 `undefined` — 지어내지 않는다. */
export function tokenFromSearch(search: string): string | undefined {
  const value = new URLSearchParams(search).get('token');
  return value !== null && value.trim() !== '' ? value : undefined;
}

/**
 * 실패를 사람 말로. **사유를 가르지 않는다** — 429 만 다르다.
 *
 * 순수 함수로 뺀다: 이 화면은 로그인 밖이라 다른 검사에서 잘 안 지나가는 자리다.
 */
export function acceptFailureText(err: unknown, retryAfterSeconds?: number): string {
  if (err instanceof ApiError && err.status === 429) {
    return t.acceptRateLimited(retryAfterSeconds ?? 60);
  }
  return t.acceptRejected;
}

export function AcceptInviteScreen({ search = window.location.search }: { search?: string }) {
  const api = useApiClient();
  const token = tokenFromSearch(search);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  const accept = useMutation({
    mutationFn: () => {
      if (token === undefined) throw new Error('no token');
      return api.acceptInvite({ token, username: username.trim(), password });
    },
    onSuccess: () => setDone(true),
  });

  if (token === undefined) {
    // 토큰 없이 열렸다. 폼을 그리면 채우고 나서야 못 쓴다는 것을 안다.
    return (
      <main className="access__accept">
        <h2>{t.acceptTitle}</h2>
        <p role="alert">{t.acceptNoToken}</p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="access__accept">
        <h2>{t.acceptTitle}</h2>
        {/* 성공을 말하고 **다음 걸음**을 준다 — 여기서 끝내면 어디로 갈지 모른다. */}
        <p role="status">{t.acceptDone}</p>
        <a href="/">{t.acceptGoSignIn}</a>
      </main>
    );
  }

  // 서버 스키마와 같은 최소 길이. 눌러 보고 400 을 받는 대신 미리 말한다.
  const passwordShort = password !== '' && password.length < 8;

  return (
    <main className="access__accept">
      <h2>{t.acceptTitle}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (username.trim() !== '' && !passwordShort && password !== '') accept.mutate();
        }}
      >
        <label>
          {t.acceptUsername}{' '}
          <input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </label>
        <label>
          {t.acceptPassword}{' '}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {passwordShort ? <p className="access__note">{t.acceptPasswordShort}</p> : null}
        <button type="submit" disabled={accept.isPending || username.trim() === '' || password === '' || passwordShort}>
          {accept.isPending ? t.acceptSubmitting : t.acceptSubmit}
        </button>
      </form>
      {accept.isError ? <p role="alert">{acceptFailureText(accept.error)}</p> : null}
    </main>
  );
}
